import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { alpha, Box, ClickAwayListener, Fade, Grid, IconButton, Popper, type PopperProps } from '@mui/material';
import { Instance as PopperInstance } from '@popperjs/core';
import Spinner from '../../../Spinner';
import ViewPolarisJobs from '@/components/Focus/AvailsSchedule/ViewPolarisJobsButton';
import { hideSidePanel, loadAvailDetails, setSelectedAvailId } from '@/redux/slices/focusCalendarSlice';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { POPPER_ZINDEX } from '@/components/Focus/configs/theme';
import { selectAvailDetailsById, selectIsAvailSelected, selectSelectedAvailId } from '@/redux/selectors/focusCalendarSelectors';
import AvailDetailsMainInfo from '@/components/_common/Table/Calendar/SidePanel/components/AvailDetailsMainInfo';
import { FocusVersionScheduling } from '@/redux/slices/slice_types';
import { LoadingStatuses } from '@/constants/constants';
import useResizeObserver from '@/components/_hooks/useResizeObserver';
import PlatformsList from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformsList';

interface Props {
  availId: number;
  children: React.ReactElement;
  calendarContainer: HTMLElement | null;
}

const AvailDetailsPopover: React.FC<Props> = ({ availId, children, calendarContainer }) => {
  const dispatch = useAppDispatch();
  const selectedAvailId = useAppSelector(selectSelectedAvailId);
  const isAvailSelected = useAppSelector(selectIsAvailSelected);
  const availDetails = useAppSelector((state) => selectAvailDetailsById(state, availId));
  const popperRef = useRef<PopperInstance | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const [popperPlacement, setPopperPlacement] = useState<'left' | 'right'>('left');
  const isLoading = availDetails?.loadingStatus === LoadingStatuses.pending;
  const details: FocusVersionScheduling | null = availDetails?.loadingStatus === LoadingStatuses.fulfilled ? availDetails.data : null;

  const { isPointerVisible } = usePointerVisibility({ anchorEl, isOpen });

  const updatePopperPosition = useCallback(() => {
    popperRef.current?.update();
  }, []);

  useResizeObserver({
    ref: { current: isOpen ? calendarContainer : null },
    onResize: updatePopperPosition
  });

  const open = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    // Load data if not loaded or failed to load previously
    if (!availDetails || availDetails?.loadingStatus === LoadingStatuses.rejected) {
      dispatch(loadAvailDetails(availId))
        .unwrap()
        .catch(() => setAnchorEl(null));
    }
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const modifiers = useMemo(
    (): PopperProps['modifiers'] => [
      {
        name: 'reportPlacement',
        enabled: true,
        phase: 'write', // runs after placement is computed
        fn: ({ state }) => {
          if (state.placement === 'left' || state.placement === 'right') {
            setPopperPlacement(state.placement);
          }
        }
      }
    ],
    []
  );

  const handleExpandButtonClick = () => {
    closePopover();
    if (isAvailSelected && selectedAvailId === availId) {
      dispatch(hideSidePanel());
    } else {
      dispatch(setSelectedAvailId(availId));
    }
  };

  return (
    <>
      <div onClick={isOpen ? closePopover : open} style={{ position: 'relative' /* required to properly position <Pointer /> */ }}>
        {children}
        {isPointerVisible && (
          <Fade in={isOpen}>
            <Pointer direction={popperPlacement === 'left' ? 'right' : 'left'} />
          </Fade>
        )}
      </div>
      <PopperStyled popperRef={popperRef} open={isOpen} anchorEl={anchorEl} modifiers={modifiers} placement="left" transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Wrapper>
              {isLoading && <Spinner size={80} />}
              {!isLoading && details && (
                <ClickAwayListener onClickAway={closePopover}>
                  <div>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs>
                        <PlatformsList platforms={details.availed_platforms || []} limit={10} />
                      </Grid>
                      <Grid item>
                        <IconButton onClick={handleExpandButtonClick}>
                          <PolarisIcon icon={isAvailSelected && selectedAvailId === availId ? 'panel_close_right' : 'panel_open_right'} />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <AvailDetailsMainInfo details={details} />

                    <Box sx={{ mt: 2 }}>
                      <ViewPolarisJobsAction variant="outlined" schedule={details} />
                    </Box>
                  </div>
                </ClickAwayListener>
              )}
            </Wrapper>
          </Fade>
        )}
      </PopperStyled>
    </>
  );
};

export default AvailDetailsPopover;

const BORDER_STYLE = `1.5px solid`;
const POPPER_PADDING = 1.75;
const POINTER_OFFSET = POPPER_PADDING + 1;

const Pointer = styled.div<{ direction: 'left' | 'right' }>`
  ${({ theme, direction }) => `
    position: absolute;
    top: 50%;
    ${direction === 'right' ? `left: -${theme.spacing(POINTER_OFFSET)};` : `right: -${theme.spacing(POINTER_OFFSET)};`}
    z-index: ${POPPER_ZINDEX + 1};
    overflow: hidden;
    clip-path: polygon(0% 0%, 100% 0, 100% 100%);
    transform: translateY(-50%) translateX(${direction === 'right' ? '-1.6px' : '1.6px'}) rotate(${direction === 'right' ? '45deg' : '225deg'});
  
    width: 16px;
    height: 16px;
    border-top: ${BORDER_STYLE} ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark};
    border-right: ${BORDER_STYLE} ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark};
    background-color: ${theme.palette.background.default};
  `}
`;

const PopperStyled = styled(Popper)`
  padding: ${({ theme }) => theme.spacing(POPPER_PADDING)};

  & > div {
    border-radius: ${({ theme }) => `${theme.shape.borderRadius * 2.4}px`};
    border: ${BORDER_STYLE} ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark)};
  }
`;

const Wrapper = styled.div`
  width: 378px;
  min-height: 300px;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const ViewPolarisJobsAction = styled(ViewPolarisJobs)`
  ${({ theme }) => {
    const actionColor = theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark;
    const hoverAlpha = theme.palette.mode === 'dark' ? 0.18 : 0.1;

    return `
      &.MuiButton-root {
        color: ${actionColor};
      }

      &.MuiButton-outlined {
        border-color: ${actionColor};
      }

      &.MuiButton-root:hover {
        background-color: ${alpha(actionColor, hoverAlpha)};
      }
    `;
  }}
`;

const usePointerVisibility = ({ anchorEl, isOpen }: { anchorEl: HTMLElement | null; isOpen: boolean }) => {
  const [isPointerVisible, setIsPointerVisible] = useState(true);

  const updatePointerVisibility = useCallback(() => {
    if (!anchorEl) return;

    const { top, height } = anchorEl.getBoundingClientRect();
    const pointerPosition = top + height / 2;
    const isCloseToEdge = pointerPosition < 35 || pointerPosition > window.innerHeight - 35;

    if (isCloseToEdge) {
      if (isPointerVisible) {
        setIsPointerVisible(false);
      }
    } else {
      if (!isPointerVisible) {
        setIsPointerVisible(true);
      }
    }
  }, [anchorEl, isPointerVisible]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updatePointerVisibility);
      window.addEventListener('resize', updatePointerVisibility);
    } else {
      window.removeEventListener('scroll', updatePointerVisibility);
      window.removeEventListener('resize', updatePointerVisibility);
    }

    return () => {
      window.removeEventListener('scroll', updatePointerVisibility);
      window.removeEventListener('resize', updatePointerVisibility);
    };
  }, [isOpen, updatePointerVisibility]);

  return { isPointerVisible };
};
